import React from 'react';
import { connect } from 'react-redux';
import { Box } from '@mui/material';

import { Typography } from '@occc/ui-design-system';
import { State as RootState } from '@occc-app/rootRedux';

import { actions, State } from './redux';
import {
  awardTypes,
  earningMechanismTypes,
  ExternalFulfillmentConfiguration,
  ExternalFulfillmentConfigurationProps,
  ExternalPointAward,
  ExternalPointAwardProps,
  LoyaltyEventConfiguration,
  LoyaltyEventConfigurationProps,
  MembershipEventConfiguration,
  MembershipEventConfigurationProps,
  NullAward,
  NullAwardProps,
  PerGroupPurchasedConfiguration,
  PerGroupPurchasedConfigurationProps,
  PerItemPurchasedConfiguration,
  PerItemPurchasedConfigurationProps,
  PerItemsAccumulatedConfiguration,
  PerItemsAccumulatedConfigurationProps,
  PerTaggedGroupPurchasedConfiguration,
  PerTaggedGroupPurchasedConfigurationProps,
  PerTaggedItemPurchasedConfiguration,
  PerTaggedItemPurchasedConfigurationProps,
  PerTaggedItemsAccumulatedConfiguration,
  PerTaggedItemsAccumulatedConfigurationProps,
  PointAward,
  PointAwardProps,
  PointsPerAward,
  PointsPerAwardProps,
  ProductPurchaseConfiguration,
  ProductPurchaseConfigurationProps,
  TotalEligiblePurchaseConfiguration,
  TotalEligiblePurchaseConfigurationProps,
  TotalEligiblePurchaseOverTimeConfiguration,
  TotalEligiblePurchaseOverTimeConfigurationProps,
  TotalSpendOnTransactionConfiguration,
  TotalSpendOnTransactionConfigurationProps,
  TotalSpendOverTimeConfiguration,
  TotalSpendOverTimeConfigurationProps,
  VarietyPurchaseConfiguration,
  VarietyPurchaseConfigurationProps,
  VarietyPurchaseOverTimeConfiguration,
  VarietyPurchaseOverTimeConfigurationProps,
  VisitsConfiguration,
  VisitsConfigurationProps,
} from './components';

import { updatableComponent } from '../updatableComponent';

// https://github.com/Microsoft/TypeScript/issues/12215#issuecomment-378589263
type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

type Actions = typeof actions;

type EMBaseProps = Pick<State, 'dirty' | 'submitting'> & {
  editMode: boolean;
  readOnly: boolean;
};

type AwardBaseProps = Pick<State, 'dirty' | 'submitting'> & {
  readOnly: boolean;
};

type Props = State &
  Actions & {
    readOnly: boolean;
    editMode: boolean;
  };

class ShowEarningMechanisms extends React.Component<Props, {}> {
  renderAward() {
    const { awardConfiguration, dirty, readOnly, selectedAward, submitting } = this.props;
    const awardBaseProps: AwardBaseProps = {
      dirty,
      readOnly,
      submitting,
    };

    switch (selectedAward) {
      case 'externalPointAward':
        type EPAProps = AwardBaseProps & Omit<ExternalPointAwardProps, 'fieldDisabled'>;
        type EPAFields = keyof State['awardConfiguration']['externalPointAward'];
        const epaProps: EPAProps = {
          ...awardBaseProps,
          ...awardConfiguration.externalPointAward,
        };
        return updatableComponent<EPAProps, EPAFields>(epaProps, [])(ExternalPointAward);
      case 'pointAward':
        type PAProps = AwardBaseProps & Omit<PointAwardProps, 'fieldDisabled'>;
        type PAFields = keyof State['awardConfiguration']['pointAward'];
        const paProps: PAProps = {
          ...awardBaseProps,
          ...awardConfiguration.pointAward,
        };
        return updatableComponent<PAProps, PAFields>(paProps, ['points'])(PointAward);
      case 'pointsPerAward':
        type PPAProps = AwardBaseProps & Omit<PointsPerAwardProps, 'fieldDisabled'>;
        type PPAFields = keyof State['awardConfiguration']['pointsPerAward'];
        const ppaProps: PPAProps = {
          ...awardBaseProps,
          ...awardConfiguration.pointsPerAward,
        };
        return updatableComponent<PPAProps, PPAFields>(ppaProps, ['points'])(PointsPerAward);
      case 'nullAward':
        type NProps = AwardBaseProps & Omit<NullAwardProps, 'fieldDisabled'>;
        type NFields = keyof State['awardConfiguration']['pointsPerAward'];
        const nProps: NProps = {
          ...awardBaseProps,
          ...awardConfiguration.nullAward,
        };
        return updatableComponent<NProps, NFields>(nProps, [])(NullAward);
      default:
        return <Box>No Award</Box>;
    }
  }

  renderEarningMechanism() {
    const { dirty, editMode, emConfiguration, readOnly, selectedEarningMechanism, submitting } =
      this.props;
    const emBaseProps: EMBaseProps = {
      dirty,
      editMode,
      readOnly,
      submitting,
    };

    switch (selectedEarningMechanism) {
      case 'externalFulfillment':
        type EFFields = keyof State['emConfiguration']['externalFulfillment'];
        type EFProps = EMBaseProps & ExternalFulfillmentConfigurationProps;
        const efProps: EFProps = {
          ...emBaseProps,
          ...emConfiguration.externalFulfillment,
        };
        return updatableComponent<EFProps, EFFields>(efProps, [])(ExternalFulfillmentConfiguration);
      case 'loyaltyEvent':
        type LEFields = keyof State['emConfiguration']['loyaltyEvent'];
        type LEProps = EMBaseProps & Omit<LoyaltyEventConfigurationProps, 'fieldDisabled'>;
        const leProps: LEProps = {
          ...emBaseProps,
          ...emConfiguration.loyaltyEvent,
        };
        return updatableComponent<LEProps, LEFields>(leProps, [])(LoyaltyEventConfiguration);
      case 'membershipEvent':
        type MEProps = EMBaseProps & Omit<MembershipEventConfigurationProps, 'fieldDisabled'>;
        type MEFields = keyof State['emConfiguration']['membershipEvent'];
        const meProps: MEProps = {
          ...emBaseProps,
          ...emConfiguration.membershipEvent,
        };
        return updatableComponent<MEProps, MEFields>(meProps, [])(MembershipEventConfiguration);
      case 'perGroupPurchased':
        type PGPProps = EMBaseProps & Omit<PerGroupPurchasedConfigurationProps, 'fieldDisabled'>;
        type PGPFields = keyof State['emConfiguration']['perGroupPurchased']['groups'][0];

        const pgpProps: PGPProps = {
          createOfferMode: false,
          ...emBaseProps,
          ...emConfiguration.perGroupPurchased,
        };
        return updatableComponent<PGPProps, PGPFields>(pgpProps, [
          'countPer',
          'countThreshold',
          'itemKey',
          'summationMode',
          'values',
        ])(PerGroupPurchasedConfiguration);
      case 'perItemPurchased':
        type PIPProps = EMBaseProps & Omit<PerItemPurchasedConfigurationProps, 'fieldDisabled'>;
        type PIPFields = keyof State['emConfiguration']['perItemPurchased'];
        const pipProps: PIPProps = {
          ...emBaseProps,
          ...emConfiguration.perItemPurchased,
        };
        return updatableComponent<PIPProps, PIPFields>(pipProps, ['values'])(
          PerItemPurchasedConfiguration,
        );
      case 'perItemsAccumulated':
        type PIAProps = EMBaseProps & Omit<PerItemsAccumulatedConfigurationProps, 'fieldDisabled'>;
        type PIAFields = keyof State['emConfiguration']['perItemsAccumulated'];
        const piaProps: PIAProps = {
          ...emBaseProps,
          ...emConfiguration.perItemsAccumulated,
        };
        return updatableComponent<PIAProps, PIAFields>(piaProps, ['values'])(
          PerItemsAccumulatedConfiguration,
        );
      case 'perTaggedGroupPurchased':
        type PTGPProps = EMBaseProps &
          Omit<PerTaggedGroupPurchasedConfigurationProps, 'fieldDisabled'>;
        type PTGPFields = keyof State['emConfiguration']['perTaggedGroupPurchased']['groups'][0];

        const ptgpProps: PTGPProps = {
          createOfferMode: false,
          ...emBaseProps,
          ...emConfiguration.perTaggedGroupPurchased,
        };
        return updatableComponent<PTGPProps, PTGPFields>(ptgpProps, [
          'countPer',
          'countThreshold',
          'summationMode',
          'tags',
        ])(PerTaggedGroupPurchasedConfiguration);
      case 'perTaggedItemPurchased':
        type PTIPProps = EMBaseProps &
          Omit<PerTaggedItemPurchasedConfigurationProps, 'fieldDisabled'>;
        type PTIPFields = keyof State['emConfiguration']['perTaggedItemPurchased'];
        const ptipProps: PTIPProps = {
          ...emBaseProps,
          ...emConfiguration.perTaggedItemPurchased,
        };
        return updatableComponent<PTIPProps, PTIPFields>(ptipProps, [
          'countPer',
          'countRounding',
          'summationMode',
          'tags',
        ])(PerTaggedItemPurchasedConfiguration);
      case 'perTaggedItemsAccumulated':
        type PTIAProps = EMBaseProps &
          Omit<PerTaggedItemsAccumulatedConfigurationProps, 'fieldDisabled'>;
        type PTIAFields = keyof State['emConfiguration']['perTaggedItemsAccumulated'];
        const ptiaProps: PTIAProps = {
          ...emBaseProps,
          ...emConfiguration.perTaggedItemsAccumulated,
        };
        return updatableComponent<PTIAProps, PTIAFields>(ptiaProps, [
          'countPer',
          'countThreshold',
          'summationMode',
          'tags',
        ])(PerTaggedItemsAccumulatedConfiguration);
      case 'productPurchase':
        type PPProps = EMBaseProps & Omit<ProductPurchaseConfigurationProps, 'fieldDisabled'>;
        type PPFields = keyof State['emConfiguration']['productPurchase'];
        const ppProps: PPProps = {
          ...emBaseProps,
          ...emConfiguration.productPurchase,
        };
        return updatableComponent<PPProps, PPFields>(ppProps, [
          'countProductsBy',
          'eligibleProductGroups',
          'purchaseThreshold',
        ])(ProductPurchaseConfiguration);
      case 'totalEligiblePurchase':
        type CPPFields = keyof State['emConfiguration']['totalEligiblePurchase'];
        type CPPProps = EMBaseProps &
          Omit<TotalEligiblePurchaseConfigurationProps, 'fieldDisabled'>;
        const cppProps: CPPProps = {
          ...emBaseProps,
          ...emConfiguration.totalEligiblePurchase,
        };
        return updatableComponent<CPPProps, CPPFields>(cppProps, [
          'countProductsBy',
          'eligibleProductGroups',
          'roundCountedProducts',
        ])(TotalEligiblePurchaseConfiguration);
      case 'totalEligiblePurchaseOverTime':
        type CPAFields = keyof State['emConfiguration']['totalEligiblePurchaseOverTime'];
        type CPAProps = EMBaseProps &
          Omit<TotalEligiblePurchaseOverTimeConfigurationProps, 'fieldDisabled'>;
        const cpaProps: CPAProps = {
          ...emBaseProps,
          ...emConfiguration.totalEligiblePurchaseOverTime,
        };
        return updatableComponent<CPAProps, CPAFields>(cpaProps, [
          'accumulationThreshold',
          'countProductsBy',
          'eligibleProductGroups',
        ])(TotalEligiblePurchaseOverTimeConfiguration);
      case 'totalSpendOnTransaction':
        type UPPFields = keyof State['emConfiguration']['totalSpendOnTransaction'];
        type UPPProps = EMBaseProps &
          Omit<TotalSpendOnTransactionConfigurationProps, 'fieldDisabled'>;
        const uppProps: UPPProps = {
          ...emBaseProps,
          ...emConfiguration.totalSpendOnTransaction,
        };
        return updatableComponent<UPPProps, UPPFields>(uppProps, [
          'ineligibleProductGroups',
          'roundMoneyAmount',
        ])(TotalSpendOnTransactionConfiguration);
      case 'totalSpendOverTime':
        type UPAFields = keyof State['emConfiguration']['totalSpendOverTime'];
        type UPAProps = EMBaseProps & Omit<TotalSpendOverTimeConfigurationProps, 'fieldDisabled'>;
        const upaProps: UPAProps = {
          ...emBaseProps,
          ...emConfiguration.totalSpendOverTime,
        };
        return updatableComponent<UPAProps, UPAFields>(upaProps, [
          'accumulationThreshold',
          'ineligibleProductGroups',
        ])(TotalSpendOverTimeConfiguration);
      case 'varietyPurchase':
        type VPProps = EMBaseProps & Omit<VarietyPurchaseConfigurationProps, 'fieldDisabled'>;
        type VPFields = keyof State['emConfiguration']['varietyPurchase']['products'][0];

        const vpProps: VPProps = {
          createOfferMode: false,
          ...emBaseProps,
          ...emConfiguration.varietyPurchase,
        };
        return updatableComponent<VPProps, VPFields>(vpProps, [
          'countProductsBy',
          'productId',
          'eligibleProductGroups',
          'purchaseThreshold',
        ])(VarietyPurchaseConfiguration);
      case 'varietyPurchaseOverTime':
        type VPOTProps = EMBaseProps &
          Omit<VarietyPurchaseOverTimeConfigurationProps, 'fieldDisabled'>;
        type VPOTFields = keyof State['emConfiguration']['varietyPurchaseOverTime']['products'][0];

        const vpotProps: VPOTProps = {
          createOfferMode: false,
          ...emBaseProps,
          ...emConfiguration.varietyPurchaseOverTime,
        };
        return updatableComponent<VPOTProps, VPOTFields>(vpotProps, [
          'countProductsBy',
          'productId',
          'eligibleProductGroups',
          'purchaseThreshold',
        ])(VarietyPurchaseOverTimeConfiguration);
      case 'visits':
        type VIProps = EMBaseProps & Omit<VisitsConfigurationProps, 'fieldDisabled'>;
        type VIFields = keyof State['emConfiguration']['visits'];
        const viProps: VIProps = {
          ...emBaseProps,
          ...emConfiguration.visits,
        };
        return updatableComponent<VIProps, VIFields>(viProps, [])(VisitsConfiguration);
      case null:
        return;
    }
  }

  renderUnconfiguredEM() {
    return (
      <Box>
        <Typography paragraph>
          Oh no! It looks like this Offer is missing an Earning Mechanism. An Earning Mechanism must
          be configured in order for Activate to properly award points to Memberships.
        </Typography>
        <Typography paragraph>
          Fortunately, Stuzo can help you fix this! Please{' '}
          <a href='https://hatchloyalty.freshdesk.com' target='_blank' rel='noreferrer'>
            submit a FreshDesk ticket
          </a>{' '}
          detailing your desired configuration and we'll take care of it for you.
        </Typography>
        <Typography paragraph>
          As a reminder, it is now possible to configure and add Earning Mechanisms and Awards to
          offers directly through API calls. Please refer to our{' '}
          <a href='https://developer.hatchloyalty.com' target='_blank' rel='noreferrer'>
            documentation
          </a>{' '}
          for more details.
        </Typography>
      </Box>
    );
  }

  renderConfiguredEM() {
    const { selectedEarningMechanism, selectedAward } = this.props;
    const earningMechanism =
      selectedEarningMechanism && earningMechanismTypes[selectedEarningMechanism].title;
    const award = selectedAward && awardTypes[selectedAward].name;

    return (
      <Box>
        <Box>
          <Typography component='h4' variant='h6' gutterBottom>
            {earningMechanism} Configuration
          </Typography>
          {this.renderEarningMechanism()}
        </Box>
        <Box>
          <Typography component='h4' variant='h6' gutterBottom>
            {award} Configuration
          </Typography>
          {this.renderAward()}
        </Box>
      </Box>
    );
  }

  render() {
    const { selectedEarningMechanism, selectedAward } = this.props;

    if (selectedEarningMechanism && selectedAward) {
      return this.renderConfiguredEM();
    } else {
      return this.renderUnconfiguredEM();
    }
  }
}

type MapStateToProps = (state: RootState) => State;
const mapStateToProps: MapStateToProps = ({
  offer: { earningMechanisms },
  productGroups: {
    data: { productGroups },
  },
}) => ({
  ...earningMechanisms,
  productGroups,
});

export default connect(mapStateToProps, actions)(ShowEarningMechanisms);
